/**
 * Default feature flag values — used until the API responds (or as permanent defaults).
 * To add a new flag, add it here and it will be available via useFeatureFlags().
 *
 * Flags come in two kinds, and FeatureFlagContext resolves them differently:
 *
 *   * GRANTED — resolved per-user from the `features` API, backed by the
 *     user_features / office_features tables. Default false; a user sees
 *     the feature only once someone grants it.
 *   * GENERALLY AVAILABLE — the feature has graduated and is on for
 *     everyone. Default true, and the context no longer consults the API
 *     for it. Kept in this interface (rather than deleted) so the call
 *     sites don't have to change, and so putting one back behind a grant
 *     is a two-line revert.
 */
export interface FeatureFlags {
  /**
   * GRANTED. Show the AI section in the navigation menu (Agents, Incoming
   * Changes). Stays grant-only — AI is a paid add-on, so offices opt in
   * via an `office_features` row rather than getting it by default.
   */
  enableAI: boolean;
  /**
   * GENERALLY AVAILABLE since the multi-office rollout. Show the
   * file-attachment UI (Attach button + attachments panel) on discussion
   * threads.
   */
  enableDiscussionAttachments: boolean;
  /**
   * GENERALLY AVAILABLE since the multi-office rollout. Show the trial
   * balance Period / Number-of-previous-periods controls under the Xero
   * Organisation field on Setup, and write the XERO TB sheet on import.
   * When off, TB imports keep the historical annuals behaviour (current
   * year + last year, XERO + XERO LY only).
   */
  enableTrialBalanceImportTypes: boolean;
}

export const defaultFeatureFlags: FeatureFlags = {
  // Grant-only — see FeatureFlagContext.
  enableAI: false,
  // Generally available. These also default true so there's no flash of
  // hidden UI between first paint and the features API responding.
  enableDiscussionAttachments: true,
  enableTrialBalanceImportTypes: true,
};
